"use client";

import React, { useMemo, useState, useEffect } from "react";
import { Worker, WorkerCard } from "@/app/components/workercard/WorkerCard";
import { SkeletonCard } from "./SkeletonCard";
import { useOrdersContext } from "@/context/OrdersContext";
import { useSearchParams } from "next/navigation";

const ADMIN_USER_IDS = [
  "cmkqxzdwi0000stuwbaz86u9b",
  "cmioc6fpo0000avif0ksyases",
];

interface WorkerMarketplaceSectionProps {
  activeTab: "my-team" | "marketplace" | undefined;
  selectedWorker: Worker | null;
  handleWorkerClick: (
    worker: Worker,
    source: "my-team" | "marketplace",
  ) => void;
  // New props required for internal logic
  searchQuery: string;
  hiredWorkerIds: string[];
  suppressLoading?: boolean;
  forceLoading?: boolean;
  // Optional props for overrides
  apiWorkers?: Worker[];
  isLoadingWorkers?: boolean;
}

export const WorkerMarketplaceSection: React.FC<
  WorkerMarketplaceSectionProps
> = ({
  activeTab,
  selectedWorker,
  handleWorkerClick,
  searchQuery,
  hiredWorkerIds,
  suppressLoading = false,
  forceLoading = false,
  apiWorkers: propApiWorkers,
  isLoadingWorkers: propIsLoadingWorkers,
}) => {
  const searchParams = useSearchParams();
  const currentWorkspaceId = searchParams?.get("workspaceId") || undefined;
  const {
    orders,
    marketplaceWorkers,
    loadingMarketplace,
    loadingOrderWorkers,
    isInitialLoading,
  } = useOrdersContext();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const readUserId = () => {
      try {
        if (typeof window !== "undefined") {
          const token = localStorage.getItem("auth_token")?.trim();
          if (!token) {
            setCurrentUserId(null);
            return;
          }
        }
        const raw =
          typeof window !== "undefined" ? localStorage.getItem("user") : null;
        if (!raw?.trim()) {
          setCurrentUserId(null);
          return;
        }
        const u = JSON.parse(raw) as { id?: string; userId?: string };
        const id = u.id ?? u.userId;
        setCurrentUserId(
          typeof id === "string" && id.trim() ? id.trim() : null,
        );
      } catch {
        setCurrentUserId(null);
      }
    };
    readUserId();
    window.addEventListener("storage", readUserId);
    window.addEventListener("auth:logout", readUserId);
    window.addEventListener("auth:loginSuccess", readUserId);
    window.addEventListener("auth:login", readUserId);
    return () => {
      window.removeEventListener("storage", readUserId);
      window.removeEventListener("auth:logout", readUserId);
      window.removeEventListener("auth:loginSuccess", readUserId);
      window.removeEventListener("auth:login", readUserId);
    };
  }, []);

  // Use context data or fall back to props for backwards compatibility
  const apiWorkers = propApiWorkers ?? marketplaceWorkers;
  const isLoadingWorkers =
    propIsLoadingWorkers !== undefined
      ? propIsLoadingWorkers
      : loadingMarketplace;

  // Mirror My Team: show skeleton while workspace/orders/services are loading
  // (e.g. after hire — orders refetch and marketplace list must update)
  const showMarketplaceLoading =
    !suppressLoading &&
    (forceLoading ||
      isInitialLoading ||
      isLoadingWorkers ||
      loadingOrderWorkers);

  // Get hired worker IDs from orders API
  const hiredWorkerIdsFromOrders = useMemo(() => {
    return orders
      .filter((order) => {
        if (!currentWorkspaceId) return true;
        return (
          (order.workspaceId || order.workspace?.id) === currentWorkspaceId
        );
      })
      .map((order) => order.serviceId);
  }, [orders, currentWorkspaceId]);

  // Combine prop-based hiredWorkerIds (for backward compatibility) with orders
  const allHiredWorkerIds = useMemo(() => {
    const propHiredWorkerIds = currentWorkspaceId ? [] : hiredWorkerIds;
    return [...new Set([...propHiredWorkerIds, ...hiredWorkerIdsFromOrders])];
  }, [hiredWorkerIds, hiredWorkerIdsFromOrders, currentWorkspaceId]);

  const filteredApiWorkers = useMemo(() => {
    return apiWorkers.filter((w) => {
      const shouldShow =
        !allHiredWorkerIds.includes(w.id) ||
        (currentUserId && ADMIN_USER_IDS.includes(currentUserId));

      if (!searchQuery.trim()) return shouldShow;

      const query = searchQuery.toLowerCase();
      return (
        shouldShow &&
        (w.name.toLowerCase().includes(query) ||
          w.role.toLowerCase().includes(query) ||
          w.description.toLowerCase().includes(query) ||
          (w.skills &&
            w.skills.some((skill) => skill.toLowerCase().includes(query))))
      );
    });
  }, [apiWorkers, allHiredWorkerIds, currentUserId, searchQuery]);

  return (
    <div>
      <div className="flex items-center justify-between px-1 transition-all duration-300 overflow-hidden opacity-100 max-h-10 mb-2">
        <span className="text-xs font-bold text-aera-gray uppercase tracking-widest">
          เพิ่มผู้ช่วย AI
        </span>
      </div>

      {/* Worker List */}
      <div className="space-y-1">
        {showMarketplaceLoading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <SkeletonCard key={i} isHired={false} />
          ))
        ) : (
          <>
            {/* API Workers */}
            {filteredApiWorkers.map((worker) => (
              <WorkerCard
                key={worker.id}
                worker={worker}
                isSelected={
                  activeTab === "marketplace" &&
                  selectedWorker?.id === worker.id
                }
                isHired={false}
                onClick={(w) => handleWorkerClick(w, "marketplace")}
              />
            ))}

            {/* No Results */}
            {filteredApiWorkers.length === 0 && !showMarketplaceLoading && (
              <div className="text-center py-6 text-xs text-aera-gray">
                ยังไม่มีบริการ
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
